import { Link, useHistory, useLocation } from "react-router-dom";
import "react-datepicker/dist/react-datepicker.css";

import { useState, useEffect, useRef } from "react";
import AllAuctions from "../../services/AuctionService";
import Pager from "../../components/Pager";
import { format } from "date-fns";
import { Table } from "../../components/Table";
import WheelSpinner from "../../components/WheelSpinner";
import TableOptions from "../components/TableOptions";
import auctionPlaceholder from "../../images/auction-placeholder.jpg";
import { SearchIcon, XIcon } from "@heroicons/react/outline";

export default function AdminAuctionListView() {
    const history = useHistory();
    const location = useLocation();
    const [auctions, setAuctions] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const [isSearching, setIsSearching] = useState(false);
    const [paginationData, setPaginationData] = useState();

    // Parse current values from URL
    const queryParams = new URLSearchParams(location.search);
    const urlPage = parseInt(queryParams.get("page")) || 1;
    const urlSearch = queryParams.get("filter[search]") || "";

    // Local state for input to avoid jumping cursor
    const [searchInput, setSearchInput] = useState(urlSearch);
    const debounceTimer = useRef(null);

    // Sync input with URL when URL changes (e.g., back/forward buttons)
    useEffect(() => {
        setSearchInput(urlSearch);
    }, [urlSearch]);

    // Update URL with new parameters
    const updateUrl = (newPage, newSearch) => {
        const params = new URLSearchParams();
        if (newPage > 1) params.set("page", newPage);
        if (newSearch) params.set("filter[search]", newSearch);

        const searchString = params.toString();
        const newUrl = searchString ? `?${searchString}` : location.pathname;
        history.replace(newUrl);
    };

    const handlePageChange = (newPage) => {
        updateUrl(newPage, urlSearch);
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchInput(value);

        // Debounce URL update
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        debounceTimer.current = setTimeout(() => {
            updateUrl(1, value);
        }, 300);
    };

    useEffect(() => {
        async function fetchAuctions() {
            setIsSearching(true);
            try {
                const params = { page: urlPage };
                if (urlSearch) {
                    params["filter[search]"] = urlSearch;
                }
                let response = await AllAuctions(params);
                setAuctions(response.data);
                setPaginationData(response.meta);
            } catch (error) {
                console.log(error);
            } finally {
                setLoading(false);
                setIsSearching(false);
            }
        }
        fetchAuctions();
    }, [urlPage, urlSearch]);

    const handleReset = () => {
        setSearchInput("");
        updateUrl(1, "");
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center mt-64 ">
                <WheelSpinner size="64" />
            </div>
        );
    }

    return (
        <>
            <div className="mb-4 relative">
                <div className="relative">
                    <input
                        type="text"
                        placeholder="Search auctions..."
                        value={searchInput}
                        onChange={handleSearchChange}
                        className="w-full px-4 py-2 pr-24 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cta focus:border-cta"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 space-x-1">
                        {isSearching ? (
                            <div className="animate-spin h-5 w-5 border-2 border-cta border-t-transparent rounded-full"></div>
                        ) : (
                            <>
                                {searchInput && (
                                    <button
                                        onClick={handleReset}
                                        className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                                        title="Clear search"
                                    >
                                        <XIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                    </button>
                                )}
                                <button
                                    className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                                    title="Search"
                                >
                                    <SearchIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
            {auctions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 px-4">
                    <SearchIcon className="h-16 w-16 text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                        No auctions found
                    </h3>
                    <p className="text-gray-500 text-center mb-4">
                        {urlSearch
                            ? `No results for "${urlSearch}". Try a different search term.`
                            : "No auctions available."}
                    </p>
                    {urlSearch && (
                        <button
                            onClick={handleReset}
                            className="px-4 py-2 bg-cta text-white rounded-md hover:bg-red-700 transition-colors"
                        >
                            Clear search
                        </button>
                    )}
                </div>
            ) : (
                <>
                    <div className={`relative overflow-hidden transition-opacity duration-200 ${isSearching ? 'opacity-50' : 'opacity-100'}`}>
                        {isSearching && (
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-shimmer pointer-events-none z-10"></div>
                        )}
                        <AdminAuctionList
                            auctions={auctions}
                            paginationData={paginationData}
                            past={false}
                        />
                    </div>
                    <Pager
                        onClickPrevious={handlePageChange}
                        onClickNext={handlePageChange}
                        {...paginationData}
                    />
                </>
            )}
        </>
    );
}

function AdminAuctionList({ auctions }) {
    return (
        <>
            <Table>
                <Table.Header>
                    <Table.HeaderCell>
                        <span className="sr-only">Auction Image</span>
                    </Table.HeaderCell>
                    <Table.HeaderCell>
                        <span className="text-sm uppercase font-medium tracking-wide">
                            Auction Name
                        </span>
                    </Table.HeaderCell>
                    <Table.HeaderCell>
                        <span className="text-sm uppercase font-medium tracking-wide">
                            Location
                        </span>
                    </Table.HeaderCell>
                    <Table.HeaderCell>
                        <span className="text-sm uppercase font-medium tracking-wide">
                            Date
                        </span>
                    </Table.HeaderCell>
                    <Table.HeaderCell>
                        <span className="sr-only">Actions</span>
                    </Table.HeaderCell>
                </Table.Header>
                <Table.Body>
                    {auctions.map((auction) => (
                        <Table.Row key={`auction_${auction.auction_number}`}>
                            <Table.Cell className="w-56">
                                <div className="py-3">
                                    <img
                                        src={
                                            auction.image_src
                                                ? auction.image_src
                                                : auctionPlaceholder
                                        }
                                        alt={auction.image_alt}
                                        className="max-h-32 min-w-36 object-cover"
                                    />
                                </div>
                            </Table.Cell>
                            <Table.Cell className="w-80 py-4">
                                <div className="flex items-center space-x-3">
                                    <div className="flex-shrink-1 w-72">
                                        <div className="font-bold text-gray-800 whitespace-normal">
                                            <Link
                                                to={`/admin/auction-interface/${auction.id}/input`}
                                                className="hover:text-cta text-sm"
                                            >
                                                {auction.title}
                                            </Link>
                                        </div>
                                        <div className="text-gray-400 text-xs whitespace-normal">
                                            {auction.tagline}
                                        </div>
                                    </div>
                                </div>
                            </Table.Cell>
                            <Table.Cell>
                                <div className="whitespace-normal text-sm font-bold w-48">
                                    {auction.location}
                                </div>
                            </Table.Cell>
                            <Table.Cell>
                                <span className="px-3">
                                    <div className="text-base font-medium">
                                        {format(
                                            parseInt(
                                                auction.auction_date_time
                                            ) * 1000,
                                            "d MMM yyyy"
                                        )}
                                    </div>
                                    <div className="text-gray-400 font-bold">
                                        {auction.auction_time}{" "}
                                        {auction.auction_timezone}
                                    </div>
                                </span>
                            </Table.Cell>
                            <Table.Cell>
                                <span className="px-6 flex items-center justify-center space-x-2">
                                    <div className="self-center">
                                        <TableOptions auctionID={auction.id} />
                                    </div>
                                </span>
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>
        </>
    );
}
